/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRouteLoaderData } from 'react-router';
import type { StJudeDigitalData } from '@/lib/tracking/stjude-datalayer';
import type { OmnitureConfig } from '@/lib/tracking/omniture-config.server';

export type StJudeRootLoaderData = {
    omnitureConfig?: OmnitureConfig;
    stJudeDigitalData?: StJudeDigitalData;
    stJudeSiteId?: string;
};

/**
 * Safe wrapper around `useRouteLoaderData<StJudeRootLoaderData>('root')`,
 * shared by every `stjude-*-datalayer-sync` component.
 *
 * `useRouteLoaderData` throws (not just returns `undefined`) when rendered
 * outside a data router — and several route component test files render
 * their route in isolation via plain `render()`, with no `RouterProvider`
 * at all (others use `createMemoryRouter`, which is fine). In the real app,
 * `root.tsx`'s data router always wraps every route, so this never actually
 * swallows a genuine bug — the catch exists purely so mounting one of these
 * sync components doesn't break an unrelated route's test suite that
 * doesn't set up a router. Every caller already no-ops when this returns
 * `undefined` (same as when `omnitureConfig`/`stJudeDigitalData` are
 * missing for any other reason), so this fits the existing fail-safe shape.
 */
export function useStJudeRootLoaderData(): StJudeRootLoaderData | undefined {
    try {
        return useRouteLoaderData<StJudeRootLoaderData>('root');
    } catch {
        return undefined;
    }
}
